import { Dialog } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import Button from '../buttons/Button';
import { acceptReservation, declineReservation } from '../../api/reservations';
import InputField from '../forms/form-elements/InputField';
import { Field, Game } from '../../types';
import { FieldInReservation } from '../../types/fields';
import { DATE_FORMATS, getRecurringDays } from '../../utils/dates';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

import LocationIcon from '../../icons/svg/location.svg';
import PlayerIcon from '../../icons/svg/player.svg';
import ClockIcon from '../../icons/svg/clock.svg';

import 'dayjs/locale/bg';
import { useResponsiveWindow } from '../../hooks/useResponsiveWindow';
import EventStatusBadge from '../ui/EventStatusBadge';
import {
  getObjectTranslation,
  getWeekDayFromNumber,
} from '../../utils/language';
import { getDayPrice } from '../../utils/getHelpers';
import CustomRadioGroup from '../forms/form-elements/CustomRadioGroup';

interface ReservationCardProps {
  game: Game;
  field: Field | FieldInReservation;
  fetchGames: () => Promise<void>;
  onClick?: () => void;
}

interface IFormInput {
  reason: string;
}

const ReservationGameCard: React.FC<ReservationCardProps> = ({
  game,
  fetchGames,
  field,
  onClick,
}) => {
  const [isOpen, setOpen] = useState(false);

  const [selectedDeclineReason, setSelectedDeclineReason] = useState<
    string | null
  >(null);

  const {
    register,
    getValues,
    setValue,
    setFocus,
    formState: { isValid },
  } = useForm<IFormInput>({
    defaultValues: {
      reason: '',
    },
  });

  const { t, i18n } = useTranslation();

  const { date, status, isExpired } = game;

  const { start, end, id, isRecurring, recurringDays, recurringEndDate } =
    game.reservation;

  const { isMobile } = useResponsiveWindow();

  const isPending = useMemo(() => status === 'PENDING', [status]);
  const formattedDate = useMemo(
    () =>
      date
        ? dayjs(date)
            .locale(i18n.language)
            .format(DATE_FORMATS.WEEK_FIRST_WITH_YEAR_SHORT)
        : '-',
    [date, i18n.language]
  );
  const formattedRecurringDate = useMemo(
    () =>
      recurringEndDate
        ? dayjs(recurringEndDate).locale(i18n.language).format('LLLL')
        : '-',
    [recurringEndDate, i18n.language]
  );

  const CancelSuggestions = useMemo(() => {
    return [
      {
        id: 1,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_1),
      },
      {
        id: 2,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_2),
      },
      {
        id: 3,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_3),
      },
      {
        id: 4,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_4),
      },
      {
        id: 5,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_5),
      },
    ];
  }, [t]);

  const openModal = useCallback(() => setOpen(true), []);

  const closeModal = useCallback(() => {
    setOpen(false);
    setSelectedDeclineReason(null);
    setValue('reason', '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [setValue]);

  const handleAcceptReservation = useCallback(async () => {
    const { data, error } = await acceptReservation(id);

    if (error) {
      const { error: errMsg } = error;

      toast(errMsg, {
        toastId: 'accept-res-error',
        theme: 'dark',
        bodyClassName: 'text-center',
      });

      return;
    }

    if (data) {
      await fetchGames();
    }
  }, [fetchGames, id]);

  const handleDeclineReservation = useCallback(async () => {
    const payload = {
      reason: getValues('reason'),
    };

    const { data, error } = await declineReservation(id, payload);

    if (error) {
      const { error: errMsg } = error;

      toast(errMsg, {
        toastId: 'accept-res-error',
        theme: 'dark',
        bodyClassName: 'text-center',
      });

      return;
    }

    if (data) {
      await fetchGames();
    }

    closeModal();
  }, [getValues, id, closeModal, fetchGames]);

  const onSelectDeclineReason = useCallback(
    (value: string | ((prevState: string | null) => string | null) | null) => {
      setSelectedDeclineReason(value);

      if (value) {
        setValue('reason', value as string, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const renderReservationDayPrices = useCallback(() => {
    if (isRecurring && recurringDays) {
      const currencyText = getObjectTranslation(
        (field as Field).currencyData,
        i18n.language
      );

      return (
        <>
          {recurringDays.map((d) => (
            <p key={d} className="text-white whitespace-nowrap">
              {`${getWeekDayFromNumber(d)} - ${getDayPrice(
                d,
                start,
                end,
                (field as Field).preferentialPrices,
                (field as Field).pricePerHour
              )} ${currencyText}`}
            </p>
          ))}
        </>
      );
    }
  }, [end, field, i18n.language, isRecurring, recurringDays, start]);

  const renderReservationDetailsInModal = () => {
    const { team, date, start, end } = game.reservation;
    const { address, name } = field;

    return (
      <>
        <div className="flex flex-row items-start gap-2">
          <img src={LocationIcon} alt="Location" className="mt-1" />
          <p className="text-white">{`${address} (${name})`}</p>
        </div>
        {team?.name && (
          <div className="flex flex-row items-center gap-2">
            <img src={PlayerIcon} alt="Team" />
            <p className="text-white">{`${team.name}`}</p>
          </div>
        )}
        <div className="flex flex-row items-center gap-2">
          <img src={ClockIcon} alt="Clock" />
          <p className="text-white">{`${
            date ? formattedDate : t(COMMON_TRANSLATE_KEYS.RECURRING)
          } (${start} - ${end})`}</p>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (isOpen) {
      setFocus('reason', { shouldSelect: true });
    }
  }, [isOpen, setFocus]);

  return (
    <div className="flex flex-col rounded-xl bg-additionalBlack">
      <div className="flex flex-col flex-grow p-4 gap-2">
        <div className="flex sm:justify-between items-start flex-row">
          <div className="flex flex-col gap-x-3 gap-y-1 sm:w-fit w-full flex-wrap">
            <p className="text-textGray flex flex-row whitespace-break-spaces gap-1">
              {isRecurring ? `${t(COMMON_TRANSLATE_KEYS.RECURRING_RESERVATION)}` : `${t(COMMON_TRANSLATE_KEYS.SINGLE_RESERVATION)}`}
            </p>
            <p className="flex flex-row whitespace-break-spaces gap-1">
              <span className="text-textGray whitespace-nowrap">
                {`${t(COMMON_TRANSLATE_KEYS.DAY).toLowerCase()}/${t(
                  COMMON_TRANSLATE_KEYS.DATE
                ).toLowerCase()}:`}
              </span>
              <span className="text-mainBlue">{formattedDate}</span>
            </p>
            <p className="flex sm:flex-row flex-col gap-1">
              <span className="text-textGray whitespace-nowrap">{`${t(
                COMMON_TRANSLATE_KEYS.TIME
              ).toLowerCase()}:`}</span>
              <span className="text-mainBlue">{`${game.start} - ${game.end}`}</span>
            </p>
          </div>
          <div className="flex items-center">
            <EventStatusBadge
              status={status}
              isExpired={isExpired}
              style={{ margin: 0 }}
            />
            {onClick && !isMobile ? (
              <EllipsisVerticalIcon
                height={30}
                onClick={onClick}
                className="cursor-pointer"
              />
            ) : (
              <></>
            )}
          </div>
        </div>

        {/* <div className="flex sm:flex-row flex-col justify-between mb-6 gap-3">
          <div className="flex flex-col">
            {team ? (
              <p>
                <span className="text-textGray">
                  {t(COMMON_TRANSLATE_KEYS.TEAM)}:{' '}
                </span>
                {team.name}
              </p>
            ) : (
              <span className="text-textGray">
                {t(COMMON_TRANSLATE_KEYS.TEAM)}: -{' '}
              </span>
            )}
            <p>
              <span className="text-textGray">
                {t(COMMON_TRANSLATE_KEYS.GAME_TYPE)}:{' '}
              </span>
              {game.isPublic
                ? ` ${t(COMMON_TRANSLATE_KEYS.PUBLIC_GAME_CONTEXT)}`
                : ` ${t(COMMON_TRANSLATE_KEYS.PRIVATE_GAME_CONTEXT)}`}
            </p>
            {isRecurring && recurringDays && (
              <p className="whitespace-break-spaces">
                <span className="text-textGray">
                  {t(COMMON_TRANSLATE_KEYS.RECURRING_DAYS)}:{' '}
                </span>
                {getRecurringDays(recurringDays).join(', ')}
              </p>
            )}
            {isRecurring && recurringEndDate && (
              <p>
                <span className="text-textGray">
                  {t(COMMON_TRANSLATE_KEYS.END_RECURRING_DAY)}:{' '}
                </span>
                {formattedRecurringDate}
              </p>
            )}
            {isRecurring && recurringRepeatCount && (
              <p>
                <span className="text-textGray">
                  {t(COMMON_TRANSLATE_KEYS.REPEAT)}:{' '}
                </span>
                {t(COMMON_TRANSLATE_KEYS.WEEK_COUNT, {
                  count: recurringRepeatCount,
                })}
              </p>
            )}
          </div>
          <div className="flex sm:flex-col gap-1 border-t sm:border-t-0 border-textGray mt-4 sm:mt-0 pt-1 sm:pt-0 w-full flex-shrink-0 sm:w-fit sm:justify-normal justify-between">
            <div className="flex justify-end">
              {' '}
              {t(COMMON_TRANSLATE_KEYS.FIELD_PAYMENT)}:
            </div>
            <div className="flex flex-col justify-end text-end"> */}
        {/* TODO: Payment */}
        {/* {!isRecurring &&
                `${price} ${getObjectTranslation(
                  (field as Field).currencyData,
                  i18n.language
                )}`}
              {isRecurring && renderReservationDayPrices()}
            </div>
          </div>
        </div> */}

        {isPending && !isExpired && (
          <div className="flex flex-row justify-between gap-x-2">
            <Button
              backgroundColor="bg-functionalRed"
              textColor="text-mainWhite"
              onClick={() => openModal()}
            >
              {t(COMMON_TRANSLATE_KEYS.DECLINE)}
            </Button>
            <Button
              backgroundColor="bg-mainGreen"
              textColor="text-mainBlack"
              onClick={async () => await handleAcceptReservation()}
            >
              {t(COMMON_TRANSLATE_KEYS.ACCEPT)}
            </Button>
          </div>
        )}

        {isMobile && onClick ? (
          <div className="flex w-full mt-5">
            <Button
              backgroundColor="bg-mainBlue"
              textColor="text-mainWhite"
              onClick={onClick}
            >
              {t(COMMON_TRANSLATE_KEYS.SEE_DETAILS)}
            </Button>
          </div>
        ) : (
          <></>
        )}

        <Dialog
          open={isOpen}
          onClose={closeModal}
          className="fixed inset-0 flex items-center justify-center z-50"
        >
          <Dialog.Overlay className="fixed inset-0 bg-black  opacity-50" />
          <div className="bg-additionalBlack p-6 rounded-xl absolute top-212 left-468 lg:max-w-[40%] mx-6">
            <div className="flex flex-row justify-between mb-3">
              <Dialog.Title className="text-white font-bold text-2xl">
                {t(COMMON_TRANSLATE_KEYS.DECLINE_RESERVATION_MODAL_TEXT)}
              </Dialog.Title>
              <button className="ps-3 pb-3 text-white" onClick={closeModal}>
                X
              </button>
            </div>
            <div className="w-full">
              {renderReservationDetailsInModal()}
              <p className="text-white text-sm mt-6 mb-4">
                {t(COMMON_TRANSLATE_KEYS.DECLINE_REASON)}
                <span className="text-functionalRed">*</span>
              </p>
            </div>
            <div className="mb-6">
              <InputField
                {...register('reason', { required: true })}
                placeholder={t(
                  COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_PLACEHOLDER
                )}
                height={3}
              />
            </div>
            <CustomRadioGroup
              items={CancelSuggestions}
              value={selectedDeclineReason}
              onChange={onSelectDeclineReason}
              className="flex flex-col gap-3 my-3"
            />
            <div className="flex justify-center">
              <Button
                textColor="text-white"
                backgroundColor="bg-functionalRed"
                width={16}
                height={3}
                onClick={async () => await handleDeclineReservation()}
                isDisabled={!isValid}
              >
                {t(COMMON_TRANSLATE_KEYS.CONFIRM)}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default ReservationGameCard;
